import React from "react";
import { Linkedin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ArrowRight, Check, Users, Target, Briefcase, Rocket, Shield, Zap } from "lucide-react";

const Index = () => {
  const ctaLink = "https://tally.so/r/ja6kA6";

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center px-6 py-24">
        {/* Subtle gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-primary/5" />
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 left-1/4 w-64 h-64 bg-accent/10 rounded-full blur-[100px]" />
        
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium mb-8">
            <Zap className="w-4 h-4" />
            Specialist AI & ML Recruitment
          </div>
          
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6 leading-[1.1]">
            We Place AI and ML Engineers Into{" "}
            <span className="text-primary">Top AI Startups</span>
          </h1>
          
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed">
            AI Hedge is a specialist AI and machine learning recruitment agency connecting elite engineers with high-growth AI startups. No job boards. No CV spam. Just high-signal placements.
          </p>
          
          <Button
            size="lg"
            className="text-lg px-8 py-6 h-auto font-semibold group"
            onClick={() => window.open(ctaLink, "_blank")}
          >
            Apply as an AI or ML Engineer
            <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
          
          <p className="text-sm text-muted-foreground mt-8">
            Trusted by founders building the next generation of AI products.
          </p>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-24 px-6 bg-muted/30">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            Who This Is For
          </h2>
          <p className="text-muted-foreground text-center mb-12 text-lg">
            AI Hedge works with engineers who want more than volume recruiting.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
            {[
              "Senior AI Engineers",
              "Machine Learning Engineers",
              "Applied AI Engineers",
              "GenAI Engineers",
              "MLOps Engineers",
              "ML Platform Engineers"
            ].map((role) => (
              <div
                key={role}
                className="flex items-center gap-3 p-4 rounded-lg bg-background border border-border"
              >
                <div className="w-2 h-2 rounded-full bg-primary" />
                <span className="font-medium">{role}</span>
              </div>
            ))}
          </div>
          
          <p className="text-center text-muted-foreground text-lg max-w-2xl mx-auto">
            If you care about real impact, strong technical teams, and meaningful ownership, AI Hedge is built for you.
          </p>
        </div>
      </section>

      {/* What Makes AI Hedge Different */}
      <section className="py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            What Makes AI Hedge Different
          </h2>
          <p className="text-muted-foreground text-center mb-12 text-lg">
            Recruitment built around signal, not noise.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { icon: Users, text: "We represent a small number of high-quality engineers at a time" },
              { icon: Target, text: "We work directly with AI-first startups and technical founders" },
              { icon: Check, text: "Every introduction is curated, relevant, and intentional" },
              { icon: Shield, text: "No mass outreach. No CV databases. No recruiter spam" }
            ].map(({ icon: Icon, text }) => (
              <div
                key={text}
                className="flex items-start gap-4 p-6 rounded-xl bg-muted/50 border border-border"
              >
                <div className="p-2 rounded-lg bg-primary/10">
                  <Icon className="w-5 h-5 text-primary" />
                </div>
                <p className="text-foreground leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
          
          <p className="text-center text-primary font-medium mt-10 text-lg">
            Our model is closer to talent representation than traditional recruitment.
          </p>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 px-6 bg-muted/30">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            How It Works
          </h2>
          <p className="text-muted-foreground text-center mb-12 text-lg">
            A simple, high-trust process.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { step: "01", title: "Apply", desc: "Share your background, interests, and constraints" },
              { step: "02", title: "Evaluate", desc: "We assess technical depth and role fit, not keyword matching" },
              { step: "03", title: "Introduce", desc: "We connect you directly to aligned AI startups" },
              { step: "04", title: "Engage", desc: "Take conversations only when there's real mutual interest" }
            ].map(({ step, title, desc }) => (
              <div key={step} className="relative">
                <div className="text-6xl font-bold text-primary/10 mb-4">{step}</div>
                <h3 className="text-xl font-semibold mb-2">{title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
          
          <p className="text-center text-muted-foreground mt-12 text-lg">
            You stay in control at every step.
          </p>
        </div>
      </section>

      {/* Types of Companies */}
      <section className="py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            Types of Companies We Place Into
          </h2>
          <p className="text-muted-foreground text-center mb-12 text-lg">
            AI-first teams building real products.
          </p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: Rocket, text: "Venture-backed AI startups" },
              { icon: Zap, text: "Applied AI and GenAI companies" },
              { icon: Briefcase, text: "ML infrastructure and platform teams" },
              { icon: Target, text: "Research-driven product teams" },
              { icon: Users, text: "AI-native SaaS companies" }
            ].map(({ icon: Icon, text }) => (
              <div
                key={text}
                className="flex items-center gap-3 p-5 rounded-lg border border-border bg-background hover:border-primary/50 transition-colors"
              >
                <Icon className="w-5 h-5 text-primary shrink-0" />
                <span>{text}</span>
              </div>
            ))}
          </div>
          
          <p className="text-center text-muted-foreground mt-10 max-w-2xl mx-auto">
            Most roles are high-impact, early-to-growth stage, and closely tied to core product development.
          </p>
        </div>
      </section>

      {/* Why Engineers Choose AI Hedge */}
      <section className="py-24 px-6 bg-muted/30">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            Why Engineers Choose AI Hedge
          </h2>
          <p className="text-muted-foreground text-center mb-12 text-lg">
            Designed for serious AI builders.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              "Fewer interviews, higher quality conversations",
              "Direct access to founders and senior technical leaders",
              "Roles that match your real skill set and growth goals",
              "Clear context on team maturity, roadmap, and expectations"
            ].map((benefit) => (
              <div
                key={benefit}
                className="flex items-center gap-4 p-5 rounded-lg bg-background border border-border"
              >
                <Check className="w-5 h-5 text-primary shrink-0" />
                <span>{benefit}</span>
              </div>
            ))}
          </div>
          
          <p className="text-center text-primary font-medium mt-10 text-lg">
            We optimize for long-term fit, not short-term placement volume.
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to explore high-signal AI roles?
          </h2>
          <p className="text-muted-foreground text-lg mb-8">
            Apply once. Get represented properly.<br />
            Let AI Hedge do the filtering and introductions.
          </p>
          
          <Button
            size="lg"
            className="text-lg px-8 py-6 h-auto font-semibold group"
            onClick={() => window.open(ctaLink, "_blank")}
          >
            Apply as an AI or ML Engineer
            <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-border">
        <div className="max-w-5xl mx-auto text-center">
          <h3 className="text-xl font-bold mb-2">AI Hedge</h3>
          <p className="text-muted-foreground text-sm">
            Specialist AI and ML Recruitment Agency
          </p>
          <p className="text-muted-foreground text-sm mb-4">
            Placing engineers into top AI startups
          </p>
          <a
            href="https://www.linkedin.com/company/ai-hedge/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-muted hover:bg-primary/10 hover:text-primary transition-colors"
            aria-label="Follow AI Hedge on LinkedIn"
          >
            <Linkedin className="w-5 h-5" />
          </a>
        </div>
      </footer>
    </div>
  );
};

export default Index;
